import React from 'react';
import type { SummaryData, SubCategorySummary, ContrastData, ContentBlock } from '../types';
import { Card } from '@shared/components';
import { SummaryContent } from './SummaryContent';
import './SummaryCard.css';

// Calculate reading time from content blocks (avg 200 words per minute)
function calculateReadingTime(contentBlocks: ContentBlock[] | null): number {
  if (!contentBlocks) return 0;
  const text = contentBlocks
    .filter((block): block is { type: 'text'; text: string } => block.type === 'text')
    .map(block => block.text)
    .join(' ');
  const wordCount = text.split(/\s+/).filter(Boolean).length;
  return Math.max(1, Math.ceil(wordCount / 200));
}

interface SummaryCardProps {
  // New format props
  summaryData: SummaryData | SubCategorySummary;
  categoryName: string;
  date: string;
  edition: string;
  generatedAt: string;
  // Contrast props
  contrast?: ContrastData | null;
  showContrast?: boolean;
}

const editionConfig: Record<string, { name: string }> = {
  morning: { name: 'Morning Edition' },
  afternoon: { name: 'Afternoon Edition' },
  evening: { name: 'Evening Edition' },
};

export function SummaryCard({
  summaryData,
  categoryName,
  date,
  edition,
  generatedAt,
  contrast,
  showContrast,
}: SummaryCardProps) {
  const [sourceSide, setSourceSide] = React.useState<'left' | 'right' | null>(null);

  React.useEffect(() => {
    const handleSourceOpen = (e: Event) => {
      const customEvent = e as CustomEvent<{ side: 'left' | 'right' }>;
      setSourceSide(customEvent.detail?.side || 'right');
    };
    const handleSourceClose = () => setSourceSide(null);

    window.addEventListener('source-detail-open', handleSourceOpen);
    window.addEventListener('source-detail-close', handleSourceClose);

    return () => {
      window.removeEventListener('source-detail-open', handleSourceOpen);
      window.removeEventListener('source-detail-close', handleSourceClose);
    };
  }, []);

  const formatDate = (dateString: string) => {
    const dateObj = new Date(dateString);
    return dateObj.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (dateString: string) => {
    const dateObj = new Date(dateString);
    return dateObj.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const { headline, contentBlocks, references, metadata } = summaryData;
  const editionInfo = editionConfig[edition] || { name: edition };

  // Get subcategory name from SubCategorySummary if present
  const subCategoryName = 'subCategory' in summaryData ? summaryData.subCategory.name : null;
  const subCategorySlug = 'subCategory' in summaryData ? summaryData.subCategory.slug : null;

  // Determine if we're showing contrast content
  const isShowingContrast = showContrast && contrast;

  // Get stats for current content
  const currentRefs = isShowingContrast ? contrast.references : references;
  const currentBlocks = isShowingContrast ? contrast.contentBlocks : contentBlocks;
  const readingTime = calculateReadingTime(currentBlocks);

  // Format model name for display (e.g., "claude-haiku-4-5-20251001" -> "Claude Haiku")
  const formatModelName = (model: string): string => {
    const match = model.match(/claude-(\w+)/i);
    if (match) {
      const variant = match[1].charAt(0).toUpperCase() + match[1].slice(1);
      return `Claude ${variant}`;
    }
    return model;
  };

  return (
    <div className={`summary-card animate-fade-in-up ${sourceSide ? `summary-card--source-${sourceSide}` : ''}`}>
      <Card variant="editorial" padding="xl">
        <div className="summary-card__content">
          <div className="summary-card__header">
            {/* Tags row */}
            <div className="summary-card__tags">
              {isShowingContrast ? (
                <span className="summary-card__contrast-tag">
                  Contrasting Perspectives
                </span>
              ) : (
                <>
                  <span className="summary-card__edition-tag">
                    {editionInfo.name}
                  </span>
                  {subCategoryName && subCategorySlug && (
                    <span className={`summary-card__leaning-tag summary-card__leaning-tag--${subCategorySlug}`}>
                      {subCategoryName}
                    </span>
                  )}
                </>
              )}
              {readingTime > 0 && (
                <span className="summary-card__reading-time">
                  {readingTime} min read
                </span>
              )}
            </div>

            {!isShowingContrast && (
              <p className="summary-card__category text-muted">
                {categoryName}
              </p>
            )}
            <h1 className="summary-card__title font-headline">
              {isShowingContrast ? contrast.headline : headline}
            </h1>
            {!isShowingContrast && (
              <p className="summary-card__date text-muted">
                {formatDate(date)}
              </p>
            )}
          </div>

          {/* Contrast highlights section */}
          {isShowingContrast && contrast.highlights && contrast.highlights.length > 0 && (
            <div className="summary-card__highlights">
              <h3 className="summary-card__section-title">Key Points of Divergence</h3>
              <div className="summary-card__highlights-grid">
                {contrast.highlights.map((highlight, index) => (
                  <div key={index} className="summary-card__highlight">
                    <h4 className="summary-card__highlight-topic">{highlight.topic}</h4>
                    <ul className="summary-card__perspectives-list">
                      {highlight.perspectives.map((perspective, pIndex) => (
                        <li key={pIndex} className="summary-card__perspective">
                          {perspective}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="summary-card__body">
            <SummaryContent
              contentBlocks={isShowingContrast ? contrast.contentBlocks : contentBlocks}
              references={isShowingContrast ? contrast.references : references}
            />
          </div>

          <div className="summary-card__footer">
            <hr className="summary-card__divider" />
            <div className="summary-card__meta">
              {metadata ? (
                <>
                  <p className="summary-card__meta-line">{isShowingContrast ? 'Analysis generated' : 'Generated'} at {formatTime(isShowingContrast ? contrast.generatedAt : generatedAt)}</p>
                  <p className="summary-card__meta-line">{metadata.headlinesEvaluated?.toLocaleString() ?? 0} headlines evaluated, {metadata.articlesProcessed} articles analyzed across {metadata.clustersProcessed} story clusters</p>
                  <p className="summary-card__meta-line">{currentRefs.length} sources cited</p>
                  <p className="summary-card__meta-line">Summarized by {formatModelName(metadata.modelUsed)}</p>
                </>
              ) : (
                <p className="summary-card__meta-line">
                  Generated at {formatTime(generatedAt)} from {currentRefs.length} sources
                </p>
              )}
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
}
